<template>
  <RdsModal :visible="props.visible" @hidden="handleClose" @close="handleCloseCross">
    <template #header>
      <div>
        <p class="h2-small fw-bold">Enter your credits</p>
        <p>Select how many credits you plan to take this semester.</p>
      </div>
    </template>

    <div class="credit-input-modal">
      <p
        class="align-self-center mb-0 px-space-xxs py-space-xxxs"
        :class="draft === 0 ? 'bg-light-3' : 'bg-secondary'"
      >
        {{ creditLoadLabel }}
      </p>

      <label class="visually-hidden" for="tuition-calculator-manual-credits">
        Credits per semester
      </label>
      <div class="credit-input-modal__value-control">
        <button
          type="button"
          class="credit-input-modal__stepper"
          aria-label="Decrease credits per semester"
          :disabled="draft === 0"
          @click="decrementCredits"
        >
          <span aria-hidden="true">−</span>
        </button>
        <div class="credit-input-modal__value-box" role="presentation" @click="focusInput">
          <input
            id="tuition-calculator-manual-credits"
            ref="inputRef"
            :value="draftDisplayValue"
            :aria-describedby="describedBy"
            :aria-invalid="!isValid"
            class="form-control credit-input-modal__input"
            :style="{ width: draftFieldWidth }"
            data-testid="manual-credit-input"
            type="number"
            min="1"
            max="18"
            step="1"
            placeholder="0"
            @input="handleInput"
            @change="handleCreditsChange"
          />
        </div>
        <button
          type="button"
          class="credit-input-modal__stepper"
          aria-label="Increase credits per semester"
          @click="incrementCredits"
        >
          <span aria-hidden="true">+</span>
        </button>
      </div>
      <p class="text-cener text-dark-1 mb-0 align-self-center">credits</p>
      <p class="text-cener fs-xs text-dark-1 mb-0 align-self-center">
        3 credits typically equals 1 course.
      </p>

      <!-- warning alert -->
      <div
        v-if="showWarning"
        id="tuition-calculator-manual-credits-warning"
        ref="warningRef"
        class="warning-banner warning-banner--warning"
        data-testid="manual-credit-warning"
        role="alert"
        aria-live="polite"
        aria-atomic="true"
      >
        <svg
          class="warning-banner__icon"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          aria-hidden="true"
          focusable="false"
          data-testid="manual-credit-warning-icon"
        >
          <path
            fill="currentColor"
            d="M8 1.33c.32 0 .62.17.79.45l6.67 11.33c.35.6-.08 1.36-.79 1.36H1.33c-.71 0-1.14-.76-.79-1.36L7.21 1.78c.17-.28.47-.45.79-.45Zm.67 9.34H7.33V12h1.34v-1.33Zm0-5.34H7.33v4h1.34v-4Z"
          />
        </svg>
        <span class="warning-banner__message">
          {{ warningText }}
        </span>
      </div>

      <div ref="actionsRef" class="credit-input-modal__actions">
        <button type="button" class="btn btn-transparent fw-normal" @click="handleCancel">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-secondary"
          data-testid="manual-credit-confirm"
          :disabled="!isValid"
          @click="handleConfirm"
        >
          Save credit load
        </button>
      </div>
    </div>
  </RdsModal>
</template>

<script setup lang="ts">
import { RdsModal } from "@rds-vue-ui/rds-modal";
import { computed, nextTick, ref, watch } from "vue";
import {
  CREDITS_RANGE_MESSAGE,
  getFullTimeCredits
} from "~/src/services/TuitionCalculatorFormService";
import type { TuitionCalculatorDegreeType } from "~/types/tuition-calculator";

const MAX_CREDIT_ENTRY = 100;

const props = defineProps<{
  visible: boolean;
  modelValue: number;
  warningMessage: string;
  degreeType: TuitionCalculatorDegreeType | null;
}>();

const emit = defineEmits<{
  (event: "update:modelValue" | "confirm" | "credits-change", value: number): void;
  (event: "close" | "close-cross" | "cancel"): void;
}>();

const inputRef = ref<HTMLInputElement | null>(null);
const warningRef = ref<HTMLElement | null>(null);
const actionsRef = ref<HTMLElement | null>(null);
const draft = ref(props.modelValue);
const closeEmitted = ref(false);
const touched = ref(false);

const draftDisplayValue = computed(() => (draft.value === 0 ? "" : String(draft.value)));

const draftFieldWidth = computed(() => `${Math.max(draftDisplayValue.value.length, 1)}ch`);

const isValid = computed(
  () => Number.isInteger(draft.value) && draft.value >= 1 && draft.value <= 18
);
const showWarning = computed(() => draft.value > 18 || (touched.value && draft.value < 1));
const warningText = computed(() =>
  draft.value > 18 ? props.warningMessage : CREDITS_RANGE_MESSAGE
);

const isMobileViewport = (): boolean => typeof window !== "undefined" && window.innerWidth < 576;

//Full time => graduate degrees  = 9 credits, undergraduate degrees = 12 credits. Max Allowed=18.
const creditLoadLabel = computed(() => {
  const fullTimeCredits = props.degreeType ? getFullTimeCredits(props.degreeType) : 12;

  if (draft.value >= fullTimeCredits && draft.value <= 18) {
    return "Full-time";
  }

  if (draft.value >= 1 && draft.value < fullTimeCredits) {
    return "Part-time";
  }
  return "Add credits";
});

const describedBy = computed(
  () =>
    [showWarning.value ? "tuition-calculator-manual-credits-warning" : null]
      .filter(Boolean)
      .join(" ") || undefined
);

const handleInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  draft.value = Math.min(MAX_CREDIT_ENTRY, Math.trunc(input.valueAsNumber || 0));
  input.value = draftDisplayValue.value;
  touched.value = true;
  emit("update:modelValue", draft.value);
};

const handleCreditsChange = () => {
  emit("credits-change", draft.value);
};

const decrementCredits = () => {
  draft.value = Math.max(0, draft.value - 1);
  emit("update:modelValue", draft.value);
};

const incrementCredits = () => {
  draft.value = Math.min(MAX_CREDIT_ENTRY, draft.value + 1);
  emit("update:modelValue", draft.value);
};

const handleConfirm = () => {
  if (!isValid.value) return;
  emit("update:modelValue", draft.value);
  emit("confirm", draft.value);
};

const handleClose = () => {
  if (closeEmitted.value) return;
  closeEmitted.value = true;
  emit("close");
};

const handleCloseCross = () => {
  emit("close-cross");
  handleClose();
};

const handleCancel = () => {
  emit("cancel");
  handleClose();
};

const focusInput = () => {
  inputRef.value?.focus();
};

watch(
  () => props.modelValue,
  (value) => {
    draft.value = value;
  }
);

watch(
  () => props.visible,
  async (visible) => {
    if (!visible) return;
    closeEmitted.value = false;
    touched.value = false;
    draft.value = props.modelValue;
    await nextTick();
    if (!isMobileViewport()) {
      inputRef.value?.focus();
    }
  },
  { immediate: true }
);

watch(showWarning, async (visible) => {
  if (!visible) return;
  await nextTick();
  warningRef.value?.scrollIntoView({ block: "nearest", behavior: "smooth" });
});
</script>

<style scoped lang="scss">
.credit-input-modal {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
}

.credit-input-modal__value-control {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.credit-input-modal__stepper {
  display: grid;
  width: 3rem;
  height: 3rem;
  place-items: center;
  border: 1px solid var(--rds-dark-2, #4a4a4a);
  background: var(--rds-white, #fff);
  color: var(--rds-dark-1, #191919);
  font-size: 1.75rem;
  line-height: 1;
}

.credit-input-modal__stepper:hover:not(:disabled) {
  background: var(--rds-gray-1, #e8e8e8);
}

.credit-input-modal__stepper:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.credit-input-modal__value-box {
  display: flex;
  flex: 0 1 10rem;
  min-width: 10rem;
  align-items: baseline;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: 1px solid var(--rds-border-color, #d8d8d8);
}

.credit-input-modal__value-box:focus-within {
  border-color: var(--rds-dark-2, #4a4a4a);
  box-shadow: 0 0 0 0.2rem rgb(0 0 0 / 12%);
}

.credit-input-modal__input {
  min-width: 1ch;
  height: auto;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--rds-dark-1, #191919);
  font-size: 2rem;
  font-weight: 700;
  text-align: left;
  box-shadow: none;
}

.credit-input-modal__input:focus {
  outline: 0;
  box-shadow: none;
}

.credit-input-modal__input::-webkit-inner-spin-button,
.credit-input-modal__input::-webkit-outer-spin-button {
  margin: 0;
  -webkit-appearance: none;
}

.credit-input-modal__input {
  -moz-appearance: textfield;
  appearance: textfield;
}

.credit-input-modal__unit {
  color: var(--rds-dark-2, #4a4a4a);
  font-size: 1.125rem;
}

.credit-input-modal :deep(.alert) {
  margin: 0;
}

.credit-input-modal__actions {
  position: sticky;
  bottom: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--rds-border-color, #d8d8d8);
  background: var(--rds-white, #fff);
}

.warning-banner {
  flex: 1 1 100%;
  margin-top: 1rem;
  border: 1px solid var(--rds-danger, #b72a2a);
  background: #fdecec;
  padding: 0.75rem 1rem;
}

.warning-banner--warning {
  display: flex;
  align-items: center;
  gap: 1rem;
  border: 1px solid #cc2f2f;
  background: #f7dddd;
  color: var(--rds-dark-3, #191919);
  font-weight: 700;
}

.warning-banner__icon {
  flex: 0 0 auto;
  color: var(--rds-dark-3, #191919);
}

.warning-banner__message {
  flex: 1 1 auto;
}

@media (max-width: 575.98px) {
  .credit-input-modal {
    max-height: min(32rem, calc(100dvh - 6rem));
    overflow-y: auto;
    padding: 1rem;
    overscroll-behavior: contain;
    scroll-padding-bottom: 5rem;
  }

  .credit-input-modal__value-control {
    gap: 0.75rem;
  }

  .credit-input-modal__value-box {
    min-width: 8rem;
  }

  .credit-input-modal__actions {
    margin-right: -1rem;
    margin-bottom: -1rem;
    margin-left: -1rem;
    padding: 1rem;
  }

  .credit-input-modal__actions > .btn {
    flex: 1 1 auto;
  }
}
</style>
